import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtMoney } from "@/lib/format";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, UserPlus, Percent } from "lucide-react";

const genKey = () => (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`);

export default function OrderCreate() {
  const nav = useNavigate();
  const { id: editId } = useParams();
  const isEdit = !!editId;
  const { settings } = useAuth();
  const cur = settings?.currency || "KMF";
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [methods, setMethods] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [items, setItems] = useState([{ _k: genKey(), service_id: "", quantity: 1, weight_kg: "" }]);
  const [payment, setPayment] = useState(0);
  const [method, setMethod] = useState("");
  const [note, setNote] = useState("");
  const [newCust, setNewCust] = useState(false);
  const [cust, setCust] = useState({ name: "", phone: "" });
  const [saving, setSaving] = useState(false);
  const [code, setCode] = useState("");
  const [appliedCode, setAppliedCode] = useState("");
  const [discountPercent, setDiscountPercent] = useState(0);
  const [breakdown, setBreakdown] = useState([]);
  const [custQuery, setCustQuery] = useState("");
  const [custOpen, setCustOpen] = useState(false);

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/services", { params: { active_only: true } }).then((r) => setServices(r.data));
    api.get("/payment-methods", { params: { active_only: true } }).then((r) => { setMethods(r.data); if (r.data[0]) setMethod(r.data[0].name); });
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/orders/${editId}`).then((r) => {
      const o = r.data;
      if (o.status === "Livré" || (o.paid || 0) > 0) {
        toast.error("Cette commande ne peut plus être modifiée (livrée ou encaissée).");
        nav(`/orders/${editId}`);
        return;
      }
      setCustomerId(o.customer_id);
      setItems((o.items || []).map((it) => ({ _k: genKey(), service_id: it.service_id, quantity: it.quantity, weight_kg: it.weight_kg || "" })));
      setNote(o.note || "");
      if (o.promo_code) { setCode(o.promo_code); setAppliedCode(o.promo_code); }
    }).catch(() => { toast.error("Commande introuvable"); nav("/orders"); });
  }, [isEdit, editId, nav]);

  // Recalcule la remise (campagne auto + parrainage + code appliqué) quand le client ou le code appliqué change
  useEffect(() => {
    if (!customerId) { setDiscountPercent(0); setBreakdown([]); return; }
    api.get("/orders/discount-preview", { params: { customer_id: customerId, code: appliedCode } })
      .then((r) => { setDiscountPercent(r.data.percent || 0); setBreakdown(r.data.breakdown || []); })
      .catch(() => { setDiscountPercent(0); setBreakdown([]); });
  }, [customerId, appliedCode]);

  const applyCode = async () => {
    if (!customerId) { toast.error("Sélectionnez d'abord un client"); return; }
    const c = code.trim().toUpperCase();
    try {
      const { data } = await api.get("/orders/discount-preview", { params: { customer_id: customerId, code: c } });
      setAppliedCode(c);
      setDiscountPercent(data.percent || 0);
      setBreakdown(data.breakdown || []);
      const codeLine = (data.breakdown || []).find((b) => b.label.startsWith("Code "));
      if (!c) toast.message("Code retiré");
      else if (codeLine) toast.success(`Code appliqué : -${codeLine.percent}%`);
      else toast.error(data.code_message || "Code invalide ou non applicable à ce client");
    } catch { toast.error("Erreur lors de l'application du code"); }
  };

  const svc = (id) => services.find((s) => s.id === id);
  const lineTotal = (it) => { const s = svc(it.service_id); return s ? s.price * (Number(it.quantity) || 0) : 0; };
  const subtotal = items.reduce((sum, it) => sum + lineTotal(it), 0);
  const discountAmount = discountPercent ? Math.round(subtotal * discountPercent) / 100 : 0;
  const total = subtotal - discountAmount;
  const remaining = Math.max(0, total - (Number(payment) || 0));

  const selCls = "w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white";

  const addItem = () => setItems([...items, { _k: genKey(), service_id: "", quantity: 1, weight_kg: "" }]);
  const rmItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const setItem = (i, key, val) => setItems(items.map((it, idx) => idx === i ? { ...it, [key]: val } : it));

  const quickCreateCustomer = async () => {
    try {
      const { data } = await api.post("/customers", cust);
      setCustomers([data, ...customers]);
      setCustomerId(data.id);
      setNewCust(false);
      toast.success("Client créé");
    } catch (e) { toast.error(apiError(e)); }
  };

  const submit = async () => {
    if (!customerId) return toast.error("Sélectionnez un client");
    if (items.some((it) => !it.service_id)) return toast.error("Sélectionnez un service pour chaque ligne");
    setSaving(true);
    try {
      const payload = {
        customer_id: customerId,
        items: items.map((it) => ({ service_id: it.service_id, quantity: Number(it.quantity), weight_kg: Number(it.weight_kg) || 0 })),
        note,
        promo_code: appliedCode,
      };
      if (isEdit) {
        const { data } = await api.put(`/orders/${editId}/full-edit`, { ...payload, initial_payment: 0, payment_method: method });
        toast.success(`Commande ${data.order_number} modifiée`);
        nav(`/orders/${editId}`);
      } else {
        const { data } = await api.post("/orders", { ...payload, initial_payment: Number(payment) || 0, payment_method: method });
        toast.success(`Commande ${data.order_number} créée`);
        nav(`/orders/${data.id}`);
      }
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5 max-w-3xl">
      <h1 className="text-2xl font-bold text-slate-800">{isEdit ? "Modifier la commande" : "Nouvelle commande"}</h1>

      {breakdown.length > 0 && (
        <div data-testid="promo-banner" className="flex items-start gap-2 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 text-sm">
          <Percent size={18} className="mt-0.5" /> <span><b>Réductions appliquées ({discountPercent}%) :</b> {breakdown.map((b) => `${b.label} (-${b.percent}%)`).join(", ")}</span>
        </div>
      )}

      <Card className="p-5 space-y-4">
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Label>Client</Label>
            {(() => {
              const selectedCust = customers.find((c) => c.id === customerId);
              const q = custQuery.trim().toLowerCase();
              const filtered = customers.filter((c) => !q || c.name.toLowerCase().includes(q) || (c.phone || "").toLowerCase().includes(q)).slice(0, 60);
              return (
                <div className="relative">
                  <Input
                    data-testid="order-customer-select"
                    autoComplete="off"
                    placeholder="Rechercher un client (nom ou numéro)…"
                    value={custOpen ? custQuery : (selectedCust ? `${selectedCust.name} · ${selectedCust.phone}` : "")}
                    onChange={(e) => { setCustQuery(e.target.value); setCustOpen(true); }}
                    onFocus={() => { setCustOpen(true); setCustQuery(""); }}
                    onBlur={() => setTimeout(() => setCustOpen(false), 150)}
                  />
                  {custOpen && (
                    <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto" data-testid="customer-options">
                      {filtered.length === 0 && <div className="px-3 py-2 text-sm text-slate-400">Aucun client trouvé</div>}
                      {filtered.map((c) => (
                        <button
                          type="button"
                          key={c.id}
                          data-testid={`customer-option-${c.id}`}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => { setCustomerId(c.id); setCustOpen(false); setCustQuery(""); }}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-sky-50 ${c.id === customerId ? "bg-sky-50" : ""}`}
                        >
                          <span className="font-medium text-slate-800">{c.name}</span> <span className="text-slate-400">· {c.phone}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
          <Button type="button" variant="outline" data-testid="toggle-new-customer" onClick={() => setNewCust(!newCust)}><UserPlus size={16} /></Button>
        </div>
        {newCust && (
          <div className="flex gap-2 items-end bg-slate-50 p-3 rounded-lg">
            <div className="flex-1"><Label>Nom</Label><Input data-testid="quick-cust-name" value={cust.name} onChange={(e) => setCust({ ...cust, name: e.target.value })} /></div>
            <div className="flex-1"><Label>Téléphone</Label><Input data-testid="quick-cust-phone" value={cust.phone} onChange={(e) => setCust({ ...cust, phone: e.target.value })} /></div>
            <Button type="button" data-testid="quick-cust-save" onClick={quickCreateCustomer} className="bg-sky-600 hover:bg-sky-700">Ajouter</Button>
          </div>
        )}
      </Card>

      <Card className="p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-slate-800">Services</h3>
          <Button type="button" size="sm" variant="outline" data-testid="add-item-btn" onClick={addItem}><Plus size={16} className="mr-1" /> Ligne</Button>
        </div>
        {items.map((it, i) => {
          const s = svc(it.service_id);
          return (
            <div key={it._k} className="flex items-end gap-2" data-testid={`order-item-${i}`}>
              <div className="flex-1">
                <Label className="text-xs">Service</Label>
                <select data-testid={`item-service-${i}`} className={selCls} value={it.service_id} onChange={(e) => setItem(i, "service_id", e.target.value)}>
                  <option value="">— Service —</option>
                  {services.map((sv) => <option key={sv.id} value={sv.id}>{sv.name} ({fmtMoney(sv.price, cur)}{sv.pricing_type === "per_kg" ? "/kg" : ""})</option>)}
                </select>
              </div>
              <div className="w-24">
                <Label className="text-xs">{s?.pricing_type === "fixed" ? "Quantité" : "Poids (kg)"}</Label>
                <Input data-testid={`item-qty-${i}`} type="number" min="0" step="0.1" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} />
              </div>
              {s?.pricing_type === "fixed" && (
                <div className="w-24">
                  <Label className="text-xs">Poids (kg)</Label>
                  <Input data-testid={`item-weight-${i}`} type="number" min="0" step="0.1" value={it.weight_kg} onChange={(e) => setItem(i, "weight_kg", e.target.value)} placeholder="fidélité" title="Poids réel du linge — compté dans la carte fidélité (n'affecte pas le prix)" />
                </div>
              )}
              <div className="w-28 text-right">
                <Label className="text-xs">Sous-total</Label>
                <p className="py-2 font-medium" data-testid={`item-subtotal-${i}`}>{fmtMoney(lineTotal(it), cur)}</p>
              </div>
              {items.length > 1 && <Button type="button" size="icon" variant="ghost" className="text-red-600" onClick={() => rmItem(i)}><Trash2 size={16} /></Button>}
            </div>
          );
        })}
      </Card>

      <Card className="p-5 space-y-4">
        {discountAmount > 0 && (
          <>
            <div className="flex justify-between text-sm"><span className="text-slate-500">Sous-total</span><span className="font-medium text-slate-700" data-testid="order-subtotal">{fmtMoney(subtotal, cur)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-slate-500">Remise (-{discountPercent}%)</span><span className="text-rose-600 font-medium" data-testid="order-discount">-{fmtMoney(discountAmount, cur)}</span></div>
          </>
        )}
        <div className="flex justify-between text-lg font-bold text-slate-800">
          <span>Total</span><span data-testid="order-total">{fmtMoney(total, cur)}</span>
        </div>
        {!isEdit && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Acompte / Paiement initial</Label>
            <Input data-testid="order-payment" type="number" min="0" value={payment} onChange={(e) => setPayment(e.target.value)} />
          </div>
          <div>
            <Label>Moyen de paiement</Label>
            <select data-testid="order-method" className={selCls} value={method} onChange={(e) => setMethod(e.target.value)}>
              {methods.map((m) => <option key={m.id} value={m.name}>{m.name}</option>)}
            </select>
          </div>
        </div>
        )}
        <div>
          <Label>Code promo / ambassadeur (optionnel)</Label>
          <div className="flex gap-2">
            <Input data-testid="order-promo-code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="ex: INSTA20, AMB15..." />
            <Button type="button" variant="outline" data-testid="apply-promo-code-btn" onClick={applyCode} className="shrink-0">{appliedCode && appliedCode === code.trim().toUpperCase() ? "Appliqué" : "Appliquer"}</Button>
          </div>
          {appliedCode && breakdown.some((b) => b.label.startsWith("Code ")) && (
            <p className="text-xs text-emerald-600 mt-1" data-testid="applied-code-info">Code « {appliedCode} » appliqué.</p>
          )}
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-slate-500">Reste à payer</span>
          <span className="font-semibold text-rose-600" data-testid="order-remaining-preview">{fmtMoney(remaining, cur)}</span>
        </div>
        <div><Label>Note</Label><Input value={note} onChange={(e) => setNote(e.target.value)} /></div>
        <Button data-testid="submit-order-btn" onClick={submit} disabled={saving} className="w-full bg-sky-600 hover:bg-sky-700">
          {isEdit ? (saving ? "Enregistrement..." : "Enregistrer les modifications") : (saving ? "Création..." : "Créer la commande")}
        </Button>
      </Card>
    </div>
  );
}
